import * as t from 'io-ts';
import {identity, isEqual, uniqBy} from 'lodash';
import memoize from 'memoize-one';

import {BumpStrategiesEnum} from '../../libs/shared/core/src/models/api/companyPreferencesModel';
import {ConversationTeamStatusesEnum} from '../../libs/shared/core/src/models/api/conversationCommonTypes';
import {
  serializeConversationOfflineMetadata,
  serializeConversationTracker,
} from '../../libs/shared/core/src/models/api/conversationPatchModel';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../libs/shared/core/src/models/api/conversationTrackerModel';
import {makeEnumRuntimeType} from '../../libs/shared/core/src/models/api/types/enumTypes';
import {makeOptional} from '../../libs/shared/core/src/models/api/types/optionalType';
import {HttpNetworkError, HttpTimeoutError} from '../../libs/shared/core/src/services/http/httpError';
import {
  composeUpdaters,
  createObjectPropertyUpdater,
  createResourceTableValueUpdater,
  createUpdater,
} from '../../libs/shared/core/src/store/helpers/updateHelpers';
import {OptimisticUpdater} from '../../libs/shared/core/src/store/optimisticUpdates/optimisticUpdatesState';
import {createCountersUpdaterForConversations} from '../../src/helpers/counterHelpers';
import {findMentionedTeammateIds} from '../../src/helpers/models/commentHelpers';
import {
  applyTrackerBumpedOfflineId,
  autoSubscribeToConversation,
  buildConversationUpdater,
  bumpConversationOnSend,
  isInternalConversation,
  maybeMakeConversationVisible,
  setConversationSubject,
  updateConversationTrackerOffline,
  updateConversationTrackers,
} from '../../src/helpers/models/conversationHelpers';
import {CommentModel, CommentStatusesEnum} from '../../src/models/api/commentModel';
import {ConversationModel} from '../../src/models/api/conversationModel';
import {findCommentLock, findConversationLock} from '../../src/services/locks/resourceLocks';
import {sendCommentApiAsync} from '../../src/store/data/comments/commentsHelpers';
import {
  saveCommentOnlineAsyncAction,
  tryRemoveCommentOffline,
} from '../../src/store/data/comments/saveCommentActions';
import {updateClientCountersForConversationsAction} from '../../src/store/data/counters/countersActions';
import {dataUpdatedAction} from '../../src/store/data/dataGroupsActions';
import {getOriginalData} from '../../src/store/data/dataSelectors';
import {DataState} from '../../src/store/data/dataState';
import {OfflineError} from '../../srcCommon/services/offline/offlineError';
import {createTaskType} from './task';

/*
 * Task.
 */

export const RSendCommentTask = createTaskType({
  name: 'sendComment',
  payloadType: t.type({
    commentUid: t.string,
    conversationUrl: t.string,
    initiatedAt: t.number,

    // The current (delegated) user tracker state for conflict resolution.
    teammateId: t.number,
    lastBumpedAt: t.number,
    offlineBumpedId: t.string,

    // The new (delegated) user tracker state.
    status: makeOptional(makeEnumRuntimeType(ConversationTrackerStatusesEnum)),

    // The mentioned teammate tracker state.
    inviteeStage: makeOptional(makeEnumRuntimeType(ConversationTrackerStagesEnum)),
  }),
  createOptimisticUpdater: (
    {commentUid, initiatedAt, teammateId, lastBumpedAt, offlineBumpedId, status},
    taskId,
  ) => {
    // Create an optimistic updater for the comment.
    const optimisticUpdaterForComment: OptimisticUpdater<DataState> = createObjectPropertyUpdater(
      'comments',
      createResourceTableValueUpdater(
        commentUid,
        createUpdater<CommentModel>((comment) => ({
          ...comment,
          status: CommentStatusesEnum.PENDING,
          // Make sure sending comments are displayed in order. Once sent, they will appear in order of the server's
          // send date (which means we should complete the API calls in in order, which should be guaranteed by the
          // offline task ordering + conversation queue locking).
          sentAt: initiatedAt,
          // This allows the undo send action to remove the offline task.
          sendOfflineTaskId: taskId,
        })),
      ),
    );

    const bindOptimisticUpdaterForConversation = memoize(
      (
        bumpStrategy: BumpStrategiesEnum,
        userId: number,
        shouldSubscribeOnComment: boolean,
        conversationId: number,
        newTrackerIds: ReadonlyArray<number>,
        newTopic: string | undefined,
      ): OptimisticUpdater<DataState> =>
        // Create an optimistic updater for the conversation.
        createCountersUpdaterForConversations(
          [conversationId],
          createObjectPropertyUpdater(
            'conversations',
            createResourceTableValueUpdater(
              conversationId,
              createUpdater<ConversationModel>((conversation) => {
                const isInternal = isInternalConversation(conversation);
                const newTrackerStage = isInternal ? ConversationTrackerStagesEnum.SUBSCRIBER : undefined;

                const isNewInternal =
                  isInternal && conversation.teamStatus === ConversationTeamStatusesEnum.INVISIBLE;
                const shouldAutoSubscribe = isNewInternal || shouldSubscribeOnComment;

                return buildConversationUpdater(
                  // If needed, become a subscriber of this conversation.
                  shouldAutoSubscribe ? autoSubscribeToConversation(userId) : identity,
                  // If needed, bump the conversation.
                  bumpConversationOnSend(bumpStrategy, BumpStrategiesEnum.COMMENT, teammateId),
                  // Add the mentioned teammates.
                  updateConversationTrackers({add: newTrackerIds, stage: newTrackerStage}),
                  // If needed, update the subject.
                  newTopic && setConversationSubject(newTopic),
                  // If needed, make the conversation visible.
                  maybeMakeConversationVisible(),
                  // Archive the conversation, if necessary.
                  updateConversationTrackerOffline({
                    teammateId,
                    lastBumpedAt,
                    offlineBumpedId,
                    status,
                  }),
                )(conversation);
              }),
            ),
          ),
        ),
      isEqual,
    );

    // Higher-order optimistic updater for the conversation.
    const optimisticUpdaterForConversation: OptimisticUpdater<DataState> = (data) => {
      const {company, userId, preferences, comments, teammates} = data;
      const comment = comments.byId[commentUid];
      if (!comment) {
        return data;
      }

      const composerTeammateIds = comment.teammateIds || [];
      const mentionedTeammateIds = findMentionedTeammateIds(userId, teammates, comment.text);
      const newTrackerIds = [...mentionedTeammateIds, ...composerTeammateIds];

      return bindOptimisticUpdaterForConversation(
        company.preferences.sendBumpStrategy,
        userId,
        preferences.shouldSubscribeOnComment,
        comment.conversationId,
        newTrackerIds,
        comment.topic,
      )(data);
    };

    return composeUpdaters(optimisticUpdaterForConversation, optimisticUpdaterForComment);
  },
  createPerformer:
    ({
      commentUid,
      conversationUrl,
      initiatedAt,
      teammateId,
      lastBumpedAt,
      offlineBumpedId,
      status,
      inviteeStage,
    }) =>
    async (dispatch, getState, finishOptimisticUpdate) => {
      try {
        // Lock the comment so our update is transactionally safe.
        await findCommentLock(commentUid).lockAsync(async () => {
          // Find the original version of the comment.
          const state = getState();
          const {userId, comments, annotations, teammates} = getOriginalData(state);
          const maybeUnsavedComment = comments.byId[commentUid];
          if (!maybeUnsavedComment) {
            return;
          }

          // If we can't find the annotation, we'll save the comment without one (if it still needs to be saved).
          const annotation =
            (maybeUnsavedComment.annotationUid && annotations.byId[maybeUnsavedComment.annotationUid]) ||
            null;

          // Save the comment, if necessary.
          const comment = maybeUnsavedComment.isSaving
            ? await dispatch(saveCommentOnlineAsyncAction(maybeUnsavedComment, annotation))
            : maybeUnsavedComment;

          // This shouldn't happen, but make sure the comment is now saved.
          if (!comment || comment.isSaving) {
            throw new Error('Saved comment could not be found in data group');
          }

          const composerTeammateIds = comment.teammateIds || [];
          const mentionedTeammateIds = findMentionedTeammateIds(userId, teammates, comment.text);
          const newTrackerIds = [...mentionedTeammateIds, ...composerTeammateIds];

          const inviteeTrackers = newTrackerIds.map((id) => ({
            teammateId: id,
            status: ConversationTrackerStatusesEnum.INBOX,
            stage: inviteeStage,
          }));
          const userTracker = {
            teammateId,
            status,
          };
          const trackers = uniqBy([userTracker, ...inviteeTrackers], (tracker) => tracker.teammateId);

          const body = {
            type: 'comment',
            comment: {uid: comment.uid},
            meta: {
              subject: comment.topic,
              trackers: trackers.map(serializeConversationTracker),
            },
            offline: serializeConversationOfflineMetadata({
              trackers: [
                {
                  teammateId,
                  lastBumpedAt,
                  offlineBumpedId,
                },
              ],
              initiatedAt,
            }),
          };

          // Lock the conversation to make sure comments to the same conversation are sent in order.
          const serverDataGroup = await findConversationLock(comment.conversationId).lockAsync(async () =>
            sendCommentApiAsync(conversationUrl, comment, body),
          );

          // Clean up client-side storage, since we may have saved the comment offline with a failed state.
          tryRemoveCommentOffline(comment.uid);

          // Post-process the API response.
          const serverDataGroupWithOfflineId = applyTrackerBumpedOfflineId(
            teammateId,
            offlineBumpedId,
            serverDataGroup,
          );
          const resultDataGroup = serverDataGroupWithOfflineId;

          // Update our state + finish the optimistic update + update client-side counters.
          // TODO: Make this prettier and reduce duplication.
          dispatch(
            updateClientCountersForConversationsAction(
              (resultDataGroup.conversations || []).map((c) => c.id),
              () => {
                finishOptimisticUpdate(dataUpdatedAction(resultDataGroup));
              },
            ),
          );
        });
      } catch (error) {
        if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
          throw new OfflineError(error);
        }

        // This will revert the comment back to its original state, which should be FAILED thanks to
        // saveCommentForSendAsync().
        throw error;
      }
    },
});
